// src/components/layout/Sidebar.js
// Global reusable sidebar component - each module passes its own items
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Settings, ChevronDown, Building, FileText, HelpCircle, Users, DollarSign, BarChart3 } from 'lucide-react';

const Sidebar = ({ 
  items = [],
  activeItem, 
  onItemClick, 
  productName = 'Product',
  user,
  userProfile,
  onBackToProducts,
  children // Allow modules to pass custom content
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(false);
  
  const handleItemClick = (itemId) => {
    if (typeof onItemClick === 'function') {
      onItemClick(itemId);
    }
  };

  const handleBackClick = () => {
    if (typeof onBackToProducts === 'function') {
      onBackToProducts();
    } else {
      window.location.href = '/my-account/dashboard';
    }
  };

  const handleAdminClick = () => {
    window.location.href = '/adminhq';
  };

  // Product menu items
  const productMenuItems = [
    {
      id: 'equity',
      name: 'Equity',
      description: 'Manage companies, shareholders, and equity',
      icon: Building,
      path: '/equity',
      color: 'blue'
    },
    {
      id: 'dataroom',
      name: 'Dealrooms',
      description: 'Secure document sharing and collaboration',
      icon: FileText,
      path: '/dealroom',
      color: 'green'
    },
    {
      id: 'capraise',
      name: 'CapRaise',
      description: 'Capital raising and investor management',
      icon: DollarSign,
      path: '/capraise',
      color: 'purple'
    },
    {
      id: 'questions',
      name: 'Questions',
      description: 'Support and knowledge management',
      icon: HelpCircle,
      path: '/questions',
      color: 'orange'
    },
    {
      id: 'userinvites',
      name: 'User Invites',
      description: 'Send invitations to team members',
      icon: Users,
      path: '/userinvites',
      color: 'indigo'
    },
    {
      id: 'marketData',
      name: 'MarketData',
      description: 'Financial data, research, and analytics',
      icon: BarChart3,
      path: '/market-data',
      color: 'teal'
    }
  ];

  const getCurrentProduct = () => {
    const currentPath = location.pathname;
    if (currentPath.startsWith('/equity')) return 'equity';
    if (currentPath.startsWith('/dealroom') || currentPath.startsWith('/dataroom') || currentPath.startsWith('/dealrooms')) return 'dataroom';
    if (currentPath.startsWith('/capraise')) return 'capraise';
    if (currentPath.startsWith('/questions')) return 'questions';
    if (currentPath.startsWith('/userinvites')) return 'userinvites';
    if (currentPath.startsWith('/market-data')) return 'marketData';
    return null;
  };

  const currentProduct = getCurrentProduct();
  const currentProductData = productMenuItems.find(item => item.id === currentProduct);

  const handleProductClick = (productId) => {
    const product = productMenuItems.find(item => item.id === productId);
    if (product) {
      navigate(product.path);
      setIsProductMenuOpen(false);
    }
  };

  // Get user role display
  // User role is now handled in UserMenu component

  return (
    <aside style={{ width: '256px', minWidth: '256px', maxWidth: '256px' }} className="bg-white border-r border-gray-200 flex flex-col h-screen">
      {/* Header with Logo and Product Name */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center mb-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">K</span>
          </div>
          <h1 className="ml-3 text-lg font-semibold text-gray-900 truncate">{productName}</h1>
        </div>
        
        {/* Product Dropdown */}
        <div className="relative">
          <button
            onClick={() => setIsProductMenuOpen(!isProductMenuOpen)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-center space-x-2">
              {currentProductData ? (
                <>
                  <currentProductData.icon className="w-4 h-4" />
                  <span className="truncate">{currentProductData.name}</span>
                </>
              ) : (
                <span>Select Product</span>
              )}
            </div>
            <ChevronDown className="w-4 h-4 flex-shrink-0" />
          </button>

          {/* Product Dropdown */}
          {isProductMenuOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
              <div className="p-2">
                <div className="space-y-1">
                  {productMenuItems.map((product) => {
                    const Icon = product.icon;
                    const isActive = product.id === currentProduct;
                    return (
                      <button
                        key={product.id}
                        onClick={() => handleProductClick(product.id)}
                        className={`w-full flex items-center space-x-3 p-2 rounded-lg text-left transition-colors ${
                          isActive
                            ? 'bg-blue-50 border border-blue-200'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                          isActive ? 'bg-blue-100' : 'bg-gray-100'
                        }`}>
                          <Icon className={`w-3 h-3 ${
                            isActive ? 'text-blue-600' : 'text-gray-600'
                          }`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${
                            isActive ? 'text-blue-900' : 'text-gray-900'
                          }`}>
                            {product.name}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 pt-2 border-t border-gray-200">
                  <button
                    onClick={handleBackClick}
                    className="w-full flex items-center space-x-2 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded transition-colors"
                  >
                    <span>← Back to Products</span>
                  </button>
                  {/* AdminHQ for Super Admins */}
                  {userProfile?.is_super_admin && (
                    <button
                      onClick={handleAdminClick}
                      className="w-full flex items-center space-x-2 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded transition-colors mt-1"
                    >
                      <Settings className="w-3 h-3" />
                      <span>Admin HQ</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Custom Content from Module (if provided) OR Standard Items */}
      {children ? (
        children
      ) : (
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto" style={{ minHeight: 0 }}>
          {items && items.map((item) => {
            const Icon = item.icon;
            const isActive = activeItem === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.id)}
                className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                {Icon && <Icon className="w-5 h-5 mr-3 flex-shrink-0" />}
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {/* Footer Section - Empty for cleaner look */}
      <div className="border-t border-gray-200">
        <div className="px-4 py-3">
          {/* Empty footer for consistent spacing */}
        </div>
      </div>

      {/* Click outside to close dropdown */}
      {isProductMenuOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsProductMenuOpen(false)}
        />
      )}
    </aside>
  );
};

export default Sidebar;